<script setup lang="ts">
import { computed, inject, onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import { type RouteParams, useRoute, useRouter } from "vue-router";

import * as tmi from "@/constants";

import { SearchContextWrapper } from "@tmi/types/context/search-context";

import Tags from "@/components/content/Tags.vue";

import { useAuthStore } from "@/stores/Auth";
import { useUserStore } from "@/stores/User";

import { useContentActions } from "@/composables/content-actions";

import type { Author, ContentfulItem, HasAuthor, Product, Tag } from "@tmi/types/entities";
import routeManager from "@/router/route-manager";
import { isFreelyAvailableContent } from "@/utils/access-utils";
import { reportContentViewEvent } from "@/composables/user-activity-monitor";
import { countTime, formatDateAsMonthDayYearString } from "@tmi/utils/date-time";

const { isAuthenticated } = storeToRefs( useAuthStore() );
const userStore = useUserStore();

const route = useRoute();
const router = useRouter();
const routeParams = computed< RouteParams >( () => route.params );

const props = defineProps< { slug: string, contentType: string } >();

const injectedContext = inject< SearchContextWrapper | undefined >( tmi.searchContextSymbol );
if ( !injectedContext ) {
  throw new Error( `The expected provided value for 'searchContextSymbol' does not exist in this context.` );
}

const { contentBySlug, setSelectedContent } = injectedContext!;

const {
  dataTipForFavoritedStatus,
  dataTipForCopyLink,
  dataTipForOpenContentInNewTab,
  favoriteIconClassNameForContent,
  handleCopyContentLink,
  handleFavoritedContentAction,
  handleOpenContentInNewTab,
  showCopyAction
} = useContentActions();

const content = ref< ContentfulItem | undefined >( contentBySlug( props.slug ) );
const contentType = ref< string | undefined >( props.contentType );
const authoredContent = ref< HasAuthor | undefined >( content.value as HasAuthor ? content.value as HasAuthor : undefined );

const contentId = computed< string | undefined >( () => content.value?.contentfulId );

const previewText = computed< string >( () => {
  // Remove any HTML tags within the main content and display the first 255 characters
  const domElement = document.createElement( 'div' );
  domElement.innerHTML = authoredContent.value?.preview ?? ``;
  return `${ domElement.innerText.trim() }...`;
} );

const title = computed< string | undefined >( () => content.value?.title );
const slug = computed< string | undefined >( () => content.value?.slug );

const publishedDate = ref< string >( formatDateAsMonthDayYearString( content.value?.publishedDate ) );
const estimatedReadingTime = computed< string >( () => countTime( authoredContent.value?.text ?? '' ) );

const author = computed< Author | null | undefined >( () => authoredContent.value?.author ?? undefined );
const authorName = computed< string >( () => author.value?.name ?? '---' );
const authorRoute = computed< string >( () => !!( author.value?.slug ) ? `${ tmi.AuthorsPath }/${ author.value?.slug }` : '#' );

const primaryProduct = computed< Product | undefined >( () => content.value?.products.slice( 0, 1 )[ 0 ] );
const primaryProductName = computed< string | undefined >( () => primaryProduct.value?.name );
const hasValidProduct = computed< boolean >( () => isAuthenticated.value && !!( primaryProduct.value?.name ) );
const productRoute = computed< string >( () => !!( primaryProduct.value ) ? routeManager.productRouteBySlug( primaryProduct.value!.slug ) : '#' );

const thumbnail = computed< string | undefined >( () => content.value?.products[ 0 ]?.favicon ?? undefined );
const previewImage = computed< string >( () => `background-image: url(${ thumbnail.value ?? favicon.value })` );
const favicon = computed< string | undefined >( () => primaryProduct.value!.favicon );
const showPreviewImage = computed< boolean >( () => ( !!thumbnail || !!favicon ) );

const tagOptions: Tag[] = [ ...( content.value?.tags ?? [] ) ].map( ( tag: Tag ) => ( { ...tag, label: tag.slug } ) );
const showTags = ref< boolean >( tagOptions.length > 0 );

function openContent() {
  setSelectedContent( contentId.value );
}

onMounted( async () => {
  try {
    content.value = contentBySlug( `${ routeParams.value.slug }` );

    if ( !( isAuthenticated.value || isFreelyAvailableContent( content.value?.products.map( p => p.slug ) ) ) ) {
      sessionStorage.setItem( 'nextLinkAfterLogin', route.path );
      await router.push( { path: routeManager.signInPath() } );
      return;
    }

    reportContentViewEvent( content.value, contentType.value! );
  } catch ( err: any ) {
    console.warn( JSON.stringify( err.message ) );
  }
} );
</script>

<template>
  <div class="article-preview article-elem">
    <div class="preview-info preview-info-top" style="display: flex; justify-content: space-between;">
      <div>
        <router-link :to="authorRoute" class="author-link">
          <span class="sin">By</span>
          <span class="text-black little-typography-atm font-bold">{{ authorName }}</span>
        </router-link>
        <div v-if="hasValidProduct" class="premiered">
          <div>
            <router-link :to="productRoute" class="bold">
              <span class="text-[transparentGray] text-xs">{{ `In ${ primaryProductName }` }}</span>
            </router-link>
          </div>
        </div>
      </div>
      <div>
        <span class="little-typography-atm text-[secondaryP]">{{ publishedDate }}</span>
        <span>
          <span class="little-typography-atm text-[secondaryP]">{{ estimatedReadingTime }}</span>
        </span>
      </div>
      <div class="shrink-0 min-w-20 space-x-3">
        <span @click.prevent="handleOpenContentInNewTab( content, contentType )" class="tooltip" :data-tip="dataTipForOpenContentInNewTab">
          <i class="pi pi-external-link"></i>
        </span>
        <template v-if="showCopyAction">
          <span @click.prevent="handleCopyContentLink( content, contentType )" class="tooltip" :data-tip="dataTipForCopyLink">
            <i class="pi pi-copy"></i>
          </span>
        </template>
        <span v-if="isAuthenticated" @click.prevent="handleFavoritedContentAction( content?.contentfulId, contentType )" class="tooltip" :data-tip="dataTipForFavoritedStatus( content?.contentfulId, contentType )">
          <i class="pi" :class="favoriteIconClassNameForContent( content?.contentfulId, contentType )"></i>
        </span>
      </div>
    </div>
    <div class="preview-block">
      <div v-if="showPreviewImage" :style="previewImage" class="preview-image preview-mask d-none d-sm-block" @click.prevent="openContent"/>
      <div class="preview-content">
        <span class="article-link" @click="openContent">
          <h4 class="text-black">{{ title }}</h4>
          <span v-if="previewText" class="description article-link" @click.prevent="openContent">
            <p class="p_small text-[secondaryP]">{{ previewText }}</p>
          </span>
        </span>
        <div v-if="showTags" class="associated-tags preview-tags">
          <Tags :tags-list="tagOptions" :slug="slug" />
        </div>
        <div class="read-more">
          <div class="btn base-btn" @click="openContent">Read more</div>
        </div>
      </div>
    </div>
  </div>
</template>
